import { defineStore } from "pinia";
import { ref, computed } from "vue";
import HomeService from "../services/home.service";
import MenuService from "../services/menu.service";
import type { Category, Product, Restaurant, MenuData } from "../types";

export const useRestaurantStore = defineStore("restaurant", () => {
  // State
  const currentRestaurant = ref<Restaurant | null>(null);
  const categories = ref<Category[]>([]);
  const products = ref<Product[]>([]);
  const menuData = ref<MenuData | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const activeCategories = computed(() =>
    categories.value.filter((c) => c.isActive)
  );

  const activeProducts = computed(() =>
    products.value.filter((p) => p.isActive)
  );

  const productsByCategory = computed(() => {
    return (categoryId: string) =>
      products.value.filter((p) => p.categoryId === categoryId);
  });

  // Actions
  async function fetchCategories(companyCode: string, restaurantCode: string) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await HomeService.getCategories(
        companyCode,
        restaurantCode
      );
      categories.value = response.data;
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to fetch categories";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchProducts(
    companyCode: string,
    restaurantCode: string,
    categoryId: string
  ) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await HomeService.getProducts(
        companyCode,
        restaurantCode,
        categoryId
      );
      products.value = response.data;
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to fetch products";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchMenu(restaurantCode: string) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await MenuService.getByRestaurant(restaurantCode);
      menuData.value = response.data;
      currentRestaurant.value = response.data.restaurant;
      categories.value = response.data.categories;
      products.value = response.data.products;
      return response.data;
    } catch (err: unknown) {
      error.value =
        (err as any).response?.data?.message || "Failed to fetch menu";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function searchMenu(query: string) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await MenuService.search(query);
      return response.data;
    } catch (err: unknown) {
      error.value = (err as any).response?.data?.message || "Search failed";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  function setCurrentRestaurant(restaurant: Restaurant) {
    currentRestaurant.value = restaurant;
  }

  function clearData() {
    currentRestaurant.value = null;
    categories.value = [];
    products.value = [];
    menuData.value = null;
    error.value = null;
  }

  return {
    // State
    currentRestaurant,
    categories,
    products,
    menuData,
    isLoading,
    error,
    // Getters
    activeCategories,
    activeProducts,
    productsByCategory,
    // Actions
    fetchCategories,
    fetchProducts,
    fetchMenu,
    searchMenu,
    setCurrentRestaurant,
    clearData,
  };
});
